import { LEAGUE_IDS, type LeagueId } from '../rating/leagues'
import type { AvatarFrameId } from '../avatar/frames'

export type LeagueCosmeticSet = {
  sleeveId: string
  matId: string
  frameId: AvatarFrameId
}

/**
 * Reaching a league in any season unlocks its set and every set below it.
 * The account keeps them after a demotion or a season rotation.
 */
export const LEAGUE_COSMETICS: Record<LeagueId, LeagueCosmeticSet> = {
  duelist: {
    sleeveId: 'league-duelist',
    matId: 'league-duelist',
    frameId: 'league-duelist',
  },
  challenger: {
    sleeveId: 'league-challenger',
    matId: 'league-challenger',
    frameId: 'league-challenger',
  },
  champion: {
    sleeveId: 'league-champion',
    matId: 'league-champion',
    frameId: 'league-champion',
  },
  king: {
    sleeveId: 'league-king',
    matId: 'league-king',
    frameId: 'league-king',
  },
}

export const LEAGUE_SLEEVE_IDS: readonly string[] = LEAGUE_IDS.map(
  (id) => LEAGUE_COSMETICS[id].sleeveId,
)
export const LEAGUE_MAT_IDS: readonly string[] = LEAGUE_IDS.map(
  (id) => LEAGUE_COSMETICS[id].matId,
)
export const LEAGUE_FRAME_IDS: readonly AvatarFrameId[] = LEAGUE_IDS.map(
  (id) => LEAGUE_COSMETICS[id].frameId,
)

export function isLeagueId(raw: unknown): raw is LeagueId {
  return typeof raw === 'string' && (LEAGUE_IDS as readonly string[]).includes(raw)
}

/** Higher of two leagues. Null means placement was never finished. */
export function higherLeague(
  a: LeagueId | null,
  b: LeagueId | null,
): LeagueId | null {
  if (!a) return b
  if (!b) return a
  return LEAGUE_IDS.indexOf(b) > LEAGUE_IDS.indexOf(a) ? b : a
}

/** True when `peak` is `league` or above it. */
export function leagueReached(peak: LeagueId | null, league: LeagueId): boolean {
  if (!peak) return false
  return LEAGUE_IDS.indexOf(peak) >= LEAGUE_IDS.indexOf(league)
}

function leagueOf(
  id: string,
  key: keyof LeagueCosmeticSet,
): LeagueId | null {
  return LEAGUE_IDS.find((league) => LEAGUE_COSMETICS[league][key] === id) ?? null
}

export function leagueOfSleeve(id: string): LeagueId | null {
  return leagueOf(id, 'sleeveId')
}

export function leagueOfMat(id: string): LeagueId | null {
  return leagueOf(id, 'matId')
}

export function leagueOfFrame(id: string): LeagueId | null {
  return leagueOf(id, 'frameId')
}
